import { ArrowDown, ArrowUp, Copy, Plus, Smartphone, Trash2, X } from 'lucide-react'

import { Boton, BotonDeIcono, clases } from '../componentes/ui'
import { CampoGenerico } from '../motor/Campo'
import { Envoltura } from '../motor/campos/Controles'
import { type Campo, valorInicial } from '../motor/esquema'
import { useFormulario, useValor } from '../motor/Formulario'
import { idDeFila, leer } from '../motor/rutas'
import { type Bloque, COLUMNAS_POR_ANCHO } from './catalogo'
import { IconoDeTipo } from './iconos'

/**
 * «Ajustes del campo»: las propiedades del campo elegido, **con los controles del motor**
 * (`CampoGenerico`) y las rutas de siempre (`campos.3.etiqueta`), así que guardar, los
 * errores junto a su control y las condiciones del núcleo funcionan igual que en el editor
 * genérico. Lo propio es el orden: lo básico arriba; cada grupo («Enseñar solo si…») en
 * su plegable; y la clave, que casi nadie toca, en «Avanzado».
 */
export const AjustesDelCampo = ({
  bloque,
  indice,
  total,
  alDuplicar,
  alQuitar,
  soloLectura,
}: {
  alDuplicar: () => void
  alQuitar: () => void
  bloque: Bloque | undefined
  indice: number
  soloLectura: boolean
  total: number
}) => {
  const { valores } = useFormulario()
  const ruta = `campos.${indice}`
  const fila = (leer(valores, ruta) ?? {}) as Record<string, unknown>
  if (!bloque) {
    return (
      <p className="text-sm text-tenue">
        Este campo es de un tipo que ya no existe ({String(fila.blockType)}). Quítalo, o pídeselo al
        equipo.
      </p>
    )
  }
  const avanzados = bloque.campos.filter((c) => c.control?.control === 'clave')
  const grupos = bloque.campos.filter(
    (c) => c.tipo === 'group' && c.nombre && !avanzados.includes(c),
  )
  const basicos = bloque.campos.filter((c) => !avanzados.includes(c) && !grupos.includes(c))
  const conReglas = (g: Campo) =>
    ((leer(fila, `${g.nombre}.reglas`) as unknown[] | undefined) ?? []).length > 0

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-boton bg-violeta-suave text-violeta">
          <IconoDeTipo tipo={bloque.slug} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-[15px] font-semibold">{bloque.etiqueta}</p>
          <p className="text-xs text-tenue">
            Campo {indice + 1} de {total}
          </p>
        </div>
        {!soloLectura && (
          <div className="flex shrink-0 items-center gap-0.5">
            <BotonDeIcono etiqueta="Duplicar este campo" onClick={alDuplicar}>
              <Copy />
            </BotonDeIcono>
            <BotonDeIcono etiqueta="Quitar este campo" onClick={alQuitar} tipo="peligro">
              <Trash2 />
            </BotonDeIcono>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4">
        {basicos.map((c, i) =>
          c.nombre === 'ancho' && (c.tipo === 'select' || c.tipo === 'radio') ? (
            <ControlDeAncho
              campo={c}
              key="ancho"
              ruta={`${ruta}.ancho`}
              soloLectura={soloLectura}
            />
          ) : c.nombre && esListaSencilla(c) ? (
            <ListaSencilla
              campo={c}
              key={c.nombre}
              ruta={`${ruta}.${c.nombre}`}
              soloLectura={soloLectura}
            />
          ) : (
            <CampoGenerico
              campo={c}
              key={c.nombre ?? `sin-nombre-${i}`}
              ruta={c.nombre ? `${ruta}.${c.nombre}` : ruta}
            />
          ),
        )}
      </div>

      {grupos.map((g) => (
        <CampoGenerico
          campo={{
            tipo: 'collapsible',
            etiqueta: g.etiqueta,
            descripcion: g.descripcion,
            campos: g.campos ?? [],
            plegable: true,
            plegado: !conReglas(g),
          }}
          key={g.nombre}
          ruta={`${ruta}.${g.nombre}`}
        />
      ))}

      {avanzados.length > 0 && (
        <CampoGenerico
          campo={{
            tipo: 'collapsible',
            etiqueta: 'Avanzado',
            campos: avanzados,
            plegable: true,
            plegado: true,
          }}
          ruta={ruta}
        />
      )}
    </div>
  )
}

/** Una lista de una o dos cosas sencillas por fila (las opciones: lo que se lee y lo que se guarda): se edita en filas, no en tarjetas. */
const esListaSencilla = (c: Campo) =>
  c.tipo === 'array' &&
  (c.campos ?? []).length > 0 &&
  (c.campos ?? []).length <= 2 &&
  (c.campos ?? []).every((s) => s.nombre && (s.tipo === 'text' || s.tipo === 'number'))

type FilaDeLista = Record<string, unknown> & { id?: string; _clave?: string }

/**
 * Las opciones de un desplegable o de unas casillas, una por fila: lo que se lee y lo que
 * se guarda lado a lado, con subir, bajar y quitar. Cada casilla es el control del motor
 * (`CampoGenerico`), así que la clave que sale de lo que se lee y los errores del núcleo
 * funcionan igual; solo se esconden las etiquetas repetidas (siguen ahí para un lector de
 * pantalla) y las ayudas, que se dicen una vez debajo.
 */
const ListaSencilla = ({
  campo,
  ruta,
  soloLectura,
}: {
  campo: Campo
  ruta: string
  soloLectura: boolean
}) => {
  const { valor, cambiar, error } = useValor<FilaDeLista[]>(ruta)
  const filas = Array.isArray(valor) ? valor : []
  const subcampos = campo.campos ?? []
  const singular = (campo.etiquetas?.singular ?? campo.etiqueta).toLowerCase()
  const minimo = campo.min ?? 0
  const ayudas = subcampos.map((s) => s.descripcion).filter(Boolean)
  const mover = (desde: number, hasta: number) => {
    if (hasta < 0 || hasta >= filas.length) return
    const copia = [...filas]
    const [fila] = copia.splice(desde, 1)
    copia.splice(hasta, 0, fila!)
    cambiar(copia)
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] font-medium text-tinta">
        {campo.etiqueta}
        {campo.obligatorio && (
          <span aria-hidden className="ml-0.5 text-naranja">
            *
          </span>
        )}
        <span className="font-normal text-tenue"> · {filas.length}</span>
      </p>
      {campo.descripcion && <p className="-mt-1 text-xs text-tenue">{campo.descripcion}</p>}
      {filas.length > 0 && (
        <div
          className="grid items-start gap-x-1.5 gap-y-1.5"
          style={{
            gridTemplateColumns: `${subcampos.map((_, i) => (i === 0 ? 'minmax(0,1.2fr)' : 'minmax(0,1fr)')).join(' ')} auto`,
          }}
        >
          {subcampos.map((s) => (
            <span
              aria-hidden
              className="text-[11px] font-medium tracking-wide text-tenue uppercase"
              key={s.nombre}
            >
              {s.etiqueta}
            </span>
          ))}
          <span />
          {filas.map((fila, i) => (
            <div className="contents" key={String(fila.id ?? fila._clave ?? i)}>
              {subcampos.map((s) => (
                <div className="min-w-0 [&_[id$=-ayuda]]:hidden [&_label]:sr-only" key={s.nombre}>
                  <CampoGenerico campo={s} ruta={`${ruta}.${i}.${s.nombre}`} />
                </div>
              ))}
              {!soloLectura ? (
                <div className="flex h-9 items-center">
                  <BotonDeIcono
                    disabled={i === 0}
                    etiqueta={`Subir «${String(fila[subcampos[0]!.nombre!] ?? '')}»`}
                    onClick={() => mover(i, i - 1)}
                    pequeno
                  >
                    <ArrowUp />
                  </BotonDeIcono>
                  <BotonDeIcono
                    disabled={i === filas.length - 1}
                    etiqueta={`Bajar «${String(fila[subcampos[0]!.nombre!] ?? '')}»`}
                    onClick={() => mover(i, i + 1)}
                    pequeno
                  >
                    <ArrowDown />
                  </BotonDeIcono>
                  <BotonDeIcono
                    disabled={filas.length <= minimo}
                    etiqueta={`Quitar «${String(fila[subcampos[0]!.nombre!] ?? '')}»`}
                    onClick={() => cambiar(filas.filter((_, j) => j !== i))}
                    pequeno
                    tipo="peligro"
                  >
                    <X />
                  </BotonDeIcono>
                </div>
              ) : (
                <span />
              )}
            </div>
          ))}
        </div>
      )}
      {error && (
        <p className="text-xs text-error" role="alert">
          {error}
        </p>
      )}
      {!soloLectura && (campo.max === undefined || filas.length < campo.max) && (
        <div>
          <Boton
            icono={<Plus />}
            onClick={() => cambiar([...filas, { ...valorInicial(subcampos), _clave: idDeFila() }])}
            pequeno
          >
            Añadir {singular}
          </Boton>
        </div>
      )}
      {ayudas.length > 0 && <p className="text-xs text-tenue">{ayudas.join(' ')}</p>}
    </div>
  )
}

/** Cuánto se ve el ancho en el dibujo de cada opción: una barra de seis. */
const Dibujo = ({ columnas }: { columnas: number }) => (
  <span aria-hidden className="grid h-2 w-full grid-cols-6 gap-px">
    {Array.from({ length: 6 }, (_, i) => (
      <span className={clases('rounded-[2px]', i < columnas ? 'bg-current' : 'bg-linea')} key={i} />
    ))}
  </span>
)

/**
 * El ancho del campo (completo, mitad, tercio, dos tercios): botones con su dibujo en vez de
 * un desplegable. Las opciones y sus nombres son las del núcleo; un ancho que el panel no
 * sepa dibujar sale como uno entero.
 */
const ControlDeAncho = ({
  campo,
  ruta,
  soloLectura,
}: {
  campo: Campo
  ruta: string
  soloLectura: boolean
}) => {
  const { valor, cambiar, error } = useValor<string>(ruta)
  const actual = valor ?? String(campo.valorPorOmision ?? 'completo')
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div
        aria-label={campo.etiqueta}
        className="grid grid-cols-2 gap-1.5 sm:grid-cols-4"
        id={ruta}
        role="radiogroup"
      >
        {(campo.opciones ?? []).map((o) => {
          const marcada = actual === o.valor
          return (
            <button
              aria-checked={marcada}
              className={clases(
                'flex flex-col items-stretch gap-1.5 rounded-boton border px-2 py-2 text-xs font-medium transition-colors',
                marcada
                  ? 'border-violeta bg-violeta-suave text-violeta'
                  : 'border-linea bg-papel text-tinta-2 hover:border-linea-fuerte',
              )}
              disabled={soloLectura}
              key={o.valor}
              onClick={() => cambiar(o.valor)}
              role="radio"
              type="button"
            >
              <Dibujo columnas={COLUMNAS_POR_ANCHO[o.valor] ?? 6} />
              {o.etiqueta}
            </button>
          )
        })}
      </div>
      <p className="mt-1 flex items-center gap-1.5 text-xs text-tenue">
        <Smartphone aria-hidden className="size-3.5" /> En un móvil, todos los campos van enteros.
      </p>
    </Envoltura>
  )
}
